import type { InspectionDetail } from "../../api/inspections";

export type Tab = "general" | "drilling" | "identity" | "spares" | "checks" | "submit";

type Tone = "done" | "warn" | "open";

export interface SectionState {
  id: Tab;
  label: string;
  /** What the section holds, in a word or two: "Matched", "9 of 33". */
  sub: string;
  tone: Tone;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Where each section of the checklist stands, from what the detail already
 * says. The steps are named, not numbered: the paper form's section numbers
 * (1, 2, 3–4, 9) are in each panel's heading, and a second numbering on the
 * steps only argued with them. Whether the inspection can be submitted is the
 * server's to say, and it says it in `problems`. */
export function sectionStates(detail: InspectionDetail): SectionState[] {
  const head = detail.inspection;
  const active = detail.valves.find((v) => v.id === detail.active_valve_id) ?? null;
  const two = detail.section_two;

  const general = [head.site_id !== null || !!head.chamber_name, !!head.work_type, !!head.inspection_date, head.team_id !== null || !!head.team_name];
  const generalMissing = general.filter((ok) => !ok).length;

  const identity = [...Object.values(detail.valve_details), ...Object.values(detail.pilot)];
  const identityFilled = identity.filter((v) => v !== null && v !== "").length;

  const answered = active?.answered ?? 0;
  const total = active?.total_checks ?? 0;
  const spares = active?.spares ?? 0;
  const blockers = detail.problems.length;

  return [
    {
      id: "general",
      label: "General",
      sub: generalMissing === 0 ? "Complete" : head.work_type ? `${generalMissing} to fill` : "Work type needed",
      tone: generalMissing === 0 ? "done" : "open",
    },
    {
      id: "drilling",
      label: "Drilling",
      sub: two.flange_match ? "Matched" : two.unresolved ? "Unresolved" : two.measurement ? "Measured" : "Not measured",
      tone: two.flange_match ? "done" : two.unresolved ? "warn" : "open",
    },
    {
      id: "identity",
      label: "Valve & pilot",
      sub: identityFilled === identity.length ? "Complete" : identityFilled === 0 ? "Not started" : `${identityFilled} of ${identity.length}`,
      tone: identityFilled === identity.length ? "done" : "open",
    },
    { id: "spares", label: "Spares", sub: spares === 0 ? "None yet" : plural(spares, "part"), tone: spares > 0 ? "done" : "open" },
    {
      id: "checks",
      label: "Checks",
      sub: `${answered} of ${total}`,
      tone: total > 0 && answered === total ? "done" : answered > 0 ? "warn" : "open",
    },
    head.is_draft
      ? { id: "submit", label: "Submit", sub: blockers === 0 ? "Ready" : `Not ready · ${blockers}`, tone: blockers === 0 ? "done" : "warn" }
      : { id: "submit", label: "Submitted", sub: "Sent", tone: "done" },
  ];
}

/** The checklist's sections as a row of steps, each saying where it stands.
 * Tabs to assistive technology, with the arrow keys moving between them. */
export default function SectionRail({ states, active, onSelect }: { states: readonly SectionState[]; active: Tab; onSelect: (tab: Tab) => void }) {
  function step(by: number) {
    const index = states.findIndex((s) => s.id === active);
    onSelect(states[(index + by + states.length) % states.length].id);
  }
  return (
    <div
      className="insp-rail-steps"
      role="tablist"
      aria-label="Sections"
      onKeyDown={(event) => {
        if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
          event.preventDefault();
          step(event.key === "ArrowRight" ? 1 : -1);
        }
      }}
    >
      {states.map((s, index) => (
        <button
          key={s.id}
          type="button"
          role="tab"
          id={`tab-${s.id}`}
          aria-selected={s.id === active}
          aria-controls={`panel-${s.id}`}
          tabIndex={s.id === active ? 0 : -1}
          className={`insp-step tone-${s.tone}`}
          onClick={() => onSelect(s.id)}
        >
          <span className="insp-step-dot" aria-hidden="true">
            {s.tone === "done" ? "✓" : index + 1}
          </span>
          <span className="insp-step-text">
            <span className="insp-step-label">{s.label}</span>
            <span className="insp-step-sub">{s.sub}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
